import { LocationStrategy } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { Constants } from 'src/@smartmanufacturing/common/constants';
import { JwtDecoderService } from '../helpers/jwt-decoder.service';
import { BaseApi } from '../api/base-api';
import { MessagingService } from './messaging.service';
import { LoggedUser } from '../models/session-users/session-users';
import { catchError, map, } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { JWTTokenValidation } from 'src/@smartmanufacturing/common/enum';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  core_features,
  lean_features,
} from 'src/@smartmanufacturing/common/custom_methods';
import { TranslaterService } from 'src/app/services/translater.service';
import { UserService } from './user-access-services/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public redirectionURL = '';

  public signInError: string = '';
  userconfiguration: any[] = [];
  defaultPlant: any;
  constructor(
    private baseApi: BaseApi,
    private http: HttpClient,
    private jwtDecoderService: JwtDecoderService,
    private router: Router,
    private messagingService: MessagingService,
    private locationStrategy: LocationStrategy,
    private toaster: ToastrService,
    private spinner: NgxSpinnerService,
    private activatedRoute: ActivatedRoute,
    public translater: TranslaterService,
    private userService: UserService
  ) { }

  signIn(signInInfo: any) {
    this.spinner.show();
    return this.http
      .post<any>(this.baseApi.encodeURL(Constants.API_SIGN_IN), signInInfo)
      .subscribe({
        next: (user) => {
          const userDetails: LoggedUser = {
            userName: user.userName,
            email: user.email,
            fullName: user.fullName,
            userCode: user.userCode,
            picture: user.picture,
            userId: user.userId,
            lastActive: user.lastActive,
            roles: user.roles,
            userDepartments: user.userDepartments,
          };
          this.baseApi.saveUserSession(userDetails);
          this.jwtDecoderService.saveToken(user.accessToken);
          this.baseApi.saveGroupSession(user.group);
          this.baseApi.saveGroupCodeSession(user.groupCode);
          this.baseApi.savePlantCodeSession(user.plantCode);
          this.baseApi.savePlantSession(user.plant);
          this.baseApi.saveUserMenusSession(user.userMenus);
          this.baseApi.saveMenuCardsSession(user.menuCards);
          this.setUserConfigurations();
          this.baseApi.saveUserPermissionSession(user.userPermissions);
          const coreFeatures = user?.userMenus
            ? user.userMenus.filter((x) => lean_features.includes(x.label))
            : [];
          // this.baseApi.saveCurrentMenuID(leanFeatures ? leanFeatures[0]?.menuId : '')
          this.baseApi.saveCurrentMenuID('');
          this.signInError = '';
          this.messagingService.editUserDevice("SignIn");
          this.navigateToInitialMenu();
          this.preventBackButton();
          this.spinner.hide();
        },
        error: ({ error }) => {
          this.signInError = '';
          this.signInError = error?.message;
          this.toaster.error('Error', error?.message);
          this.spinner.hide();
        },
      });
  }
  signInWithUserUsingToken(info: any, reload: boolean) {
    this.spinner.show();
    const token = info.token;
    this.jwtDecoderService.saveToken(token);
    return this.http
      .get<any>(this.baseApi.encodeURL(Constants.API_SIGN_IN_USER_USING_TOKEN))
      .subscribe({
        next: (user) => {
          const userDetails: LoggedUser = {
            userName: user.userName,
            email: user.email,
            fullName: user.fullName,
            userCode: user.userCode,
            picture: user.picture,
            userId: user.userId,
            lastActive: user.lastActive,
            roles: user.roles,
            userDepartments: user.userDepartments,
          };
          this.baseApi.saveUserSession(userDetails);
          this.jwtDecoderService.saveToken(user.accessToken);
          this.baseApi.saveGroupSession(user.group);
          this.baseApi.saveGroupCodeSession(user.groupCode);
          this.baseApi.saveLanguage(info.languageName);
          this.baseApi.savePlantCodeSession(user.plantCode);
          this.translater.switchLang(info.languageName);
          this.baseApi.savePlantSession(user.plant);
          this.baseApi.saveUserMenusSession(user.userMenus);
          this.baseApi.saveMenuCardsSession(user.menuCards);
          this.baseApi.saveUserPermissionSession(user.userPermissions);
          this.setUserConfigurations();
          const leanFeatures = user?.userMenus
            ? user.userMenus.filter((x) => lean_features.includes(x.label))
            : [];
          this.baseApi.saveCurrentMenuID(
            leanFeatures ? leanFeatures[0]?.menuId : ''
          );
          this.navigateToInitialMenu();
          this.preventBackButton();

          this.signInError = '';
          this.messagingService.editUserDevice("SignIn");
          this.spinner.hide();
          if (!reload) {
            this.baseApi.saveCurrentMenuID(info?.menuId);
            // if (info?.navigateToMenu) {
            const route = user.userPermissions.find(
              (x) => x.menuId == info?.menuId
            )?.route;
            this.router
              .navigateByUrl('/', { skipLocationChange: true })
              .then(() => {
                this.router.navigate([route]);
              });
            // }
          }
          if (reload) {
            setTimeout(() => {
              document.location.reload();
            }, 1000);
          }
        },
        error: ({ error }) => {
          this.signInError = '';
          this.signInError = error?.message;
          this.toaster.error('Error', error?.message);
          this.spinner.hide();
        },
      });
  }
  signInWithUser(info: any, reload: boolean) {
    this.spinner.show();
    return this.http
      .post<any>(this.baseApi.encodeURL(Constants.API_SIGN_IN_USER), info)
      .subscribe({
        next: (user) => {
          const userDetails: LoggedUser = {
            userName: user.userName,
            email: user.email,
            fullName: user.fullName,
            userCode: user.userCode,
            picture: user.picture,
            userId: user.userId,
            lastActive: user.lastActive,
            roles: user.roles,
            userDepartments: user.userDepartments,
          };
          this.baseApi.saveUserSession(userDetails);
          this.jwtDecoderService.saveToken(user.accessToken);
          this.baseApi.saveGroupSession(user.group);
          this.baseApi.saveGroupCodeSession(user.groupCode);
          this.baseApi.saveLanguage(info.languageName);
          this.baseApi.savePlantCodeSession(user.plantCode);
          this.translater.switchLang(info.languageName);
          this.baseApi.savePlantSession(user.plant);
          this.baseApi.saveUserMenusSession(user.userMenus);
          this.baseApi.saveMenuCardsSession(user.menuCards);
          this.baseApi.saveUserPermissionSession(user.userPermissions);
          this.setUserConfigurations();
          const leanFeatures = user?.userMenus
            ? user.userMenus.filter((x) => lean_features.includes(x.label))
            : [];
          this.baseApi.saveCurrentMenuID(
            leanFeatures ? leanFeatures[0]?.menuId : ''
          );
          this.navigateToInitialMenu();
          this.preventBackButton();

          this.signInError = '';
          this.messagingService.editUserDevice("SignIn");
          this.spinner.hide();
          if (!reload) {
            this.baseApi.saveCurrentMenuID(info?.menuId);
            // if (info?.navigateToMenu) {
            const route = user.userPermissions.find(
              (x) => x.menuId == info?.menuId
            )?.route;
            this.router
              .navigateByUrl('/', { skipLocationChange: true })
              .then(() => {
                this.router.navigate([route]);
              });
            // }
          }
          if (reload) {
            setTimeout(() => {
              document.location.reload();
            }, 1000);
          }
        },
        error: ({ error }) => {
          this.signInError = '';
          this.signInError = error?.message;
          this.toaster.error('Error', error?.message);
          this.spinner.hide();
        },
      });
  }
  signInWithPlantAdmin(info: any): Observable<any> {
    return this.http
      .post<any>(
        this.baseApi.encodeURL(Constants.API_SIGN_IN_WITH_PLANT_ADMIN),
        info
      )
      .pipe(
        map(
          (user) => {
            const userDetails: LoggedUser = {
              userName: user.userName,
              email: user.email,
              fullName: user.fullName,
              userCode: user.userCode,
              picture: user.picture,
              userId: user.userId,
              lastActive: user.lastActive,
              roles: user.roles,
              userDepartments: user.userDepartments,
            };
            this.baseApi.saveUserSession(userDetails);
            this.jwtDecoderService.saveToken(user.accessToken);
            this.baseApi.saveGroupSession(user.group);
            this.baseApi.saveLanguage(info.languageName);
            this.translater.switchLang(info.languageName);
            this.baseApi.saveGroupCodeSession(user.groupCode);
            this.baseApi.savePlantCodeSession(user.plantCode);
            this.baseApi.savePlantSession(user.plant);
            this.baseApi.saveUserMenusSession(user.userMenus);
            this.baseApi.saveMenuCardsSession(user.menuCards);
            this.setUserConfigurations();
            this.baseApi.saveUserPermissionSession(user.userPermissions);
            this.baseApi.saveCurrentMenuID(
              user?.userMenus ? user?.userMenus[0]?.menuId : ''
            );
            this.navigateToInitialMenu();
            this.preventBackButton();

            this.signInError = '';
            this.messagingService.editUserDevice("SignIn");
            setTimeout(() => {
              document.location.reload();
            }, 1000);
          },
          ({ error }) => {
            this.signInError = '';
            this.signInError = error?.message;
            this.toaster.error('Error', error?.message);
          }
        ),
        catchError((error) => {
          console.log(error);
          return of(error);
        })
      );
  }

  switchAccount(info: any) {
    return this.http
      .post<any>(
        this.baseApi.encodeURL(Constants.API_SIGN_IN_SWITCH_ACCOUNT),
        info
      )
      .subscribe({
        next: (user) => {
          const userDetails: LoggedUser = {
            userName: user.userName,
            email: user.email,
            fullName: user.fullName,
            userCode: user.userCode,
            picture: user.picture,
            userId: user.userId,
            lastActive: user.lastActive,
            roles: user.roles,
            userDepartments: user.userDepartments,
          };
          this.baseApi.saveUserSession(userDetails);
          this.jwtDecoderService.saveToken(user.accessToken);
          this.baseApi.saveGroupSession(user.group);
          this.baseApi.saveLanguage(info.languageName);
          this.translater.switchLang(info.languageName);
          this.baseApi.saveGroupCodeSession(user.groupCode);
          this.baseApi.savePlantCodeSession(user.plantCode);
          this.baseApi.savePlantSession(user.plant);
          this.baseApi.saveUserMenusSession(user.userMenus);
          this.baseApi.saveMenuCardsSession(user.menuCards);
          this.setUserConfigurations();
          this.baseApi.saveUserPermissionSession(user.userPermissions);
          const leanFeatures = user?.userMenus
            ? user.userMenus.filter((x) => lean_features.includes(x.label))
            : [];
          this.baseApi.saveCurrentMenuID(
            leanFeatures ? leanFeatures[0]?.menuId : ''
          );
          this.navigateToInitialMenu();
          this.preventBackButton();

          this.signInError = '';
          this.messagingService.editUserDevice("SignIn");
          setTimeout(() => {
            document.location.reload();
          }, 1000);
        },
        error: ({ error }) => {
          this.signInError = '';
          this.signInError = error?.message;
          this.toaster.error('Error', error?.message);
        },
      });
  }
  changeUserEmail(data: any): Observable<any> {
    return this.http.post<any>(
      this.baseApi.encodeURL(Constants.API_CHANGE_USER_EMAIL),
      data
    );
  }
  resendVerificationCode(email: string): Observable<any> {
    return this.http.get<any>(
      this.baseApi.encodeURL(Constants.API_RESEND_VERIFICATION_CODE) +
      '/' +
      email
    );
  }
  resendVerificationCodeFormGroupAdmin(grpCode: string): Observable<any> {
    return this.http.get<any>(
      this.baseApi.encodeURL(Constants.API_RESEND_VERIFICATION_CODE_GROUP) +
      '/' +
      grpCode
    );
  }
  resendVerificationCodeFormPlantAdmin(pltCode: string): Observable<any> {
    return this.http.get<any>(
      this.baseApi.encodeURL(Constants.API_RESEND_VERIFICATION_CODE_PLANT) +
      '/' +
      pltCode
    );
  }
  confirmEmail(email: string, token: string): Observable<any> {
    return this.http.post<any>(
      this.baseApi.encodeURL(Constants.API_CONFIRM_EMAIL),
      {
        email,
        token,
      }
    );
  }

  // public navigateToInitialMenu(): void {
  //     this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
  //   this.router.navigate(['Home']));
  // }

  // public navigateToInitialMenu(): void {
  //   this.activatedRoute.queryParams.subscribe((x) => {
  //     if (x.docKey) {
  //       localStorage.setItem('O3RightMenuId', 'M-247');
  //       this.router.navigate(['/DM/Documents'], {
  //         queryParams: x.filterText
  //           ? {
  //             docKey: x.docKey,
  //             mailType: x.mailType,
  //             filterText: x.filterText,
  //             revisionNo: x.revisionNo,
  //           }
  //           : { docKey: x.docKey, mailType: x.mailType },
  //       });
  //     } else if (x.mainDoc) {
  //       localStorage.setItem('O3RightMenuId', 'M-247');
  //       this.router.navigate(['/DM/Documents'], {
  //         queryParams: { draft: true, ptId: x.ptId, ref: x.ref },
  //       });
  //     } else if (x.inactiveDoc) {
  //       localStorage.setItem('O3RightMenuId', 'M-247');
  //       this.router.navigate(['/DM/Documents'], {
  //         queryParams: { inactiveDoc: true, ptId: x.ptId, ref: x.ref },
  //       });
  //     } else {
  //       // this.router.navigate(['/home']);
  //       this.router.navigate(['/landingPage']);
  //     }
  //   });
  // }
  public navigateToInitialMenu(): void {
   
    const x = this.activatedRoute.snapshot.queryParams;
    if (x['docKey']) {
      localStorage.setItem('O3RightMenuId', 'M-247');
      localStorage.setItem('current_menu_id', 'M-247');
      this.router.navigate(['/DM/Documents'], {
        queryParams: x['filterText']
          ? {
            docKey: x['docKey'],
            mailType: x['mailType'],
            filterText: x['filterText'],
            revisionNo: x['revisionNo'],
          }
          : { docKey: x['docKey'], mailType: x['mailType'], revisionNo: x['revisionNo'] },
      });
    } else if (x['mainDoc']) {
      localStorage.setItem('O3RightMenuId', 'M-247');
      localStorage.setItem('current_menu_id', 'M-247');
      const queryParams = new URLSearchParams({
        draft: 'true',
        ptId: 'O-2372838648201',
        ref: x['ref'] || ''
      });
      const fullUrl = `/DM/Documents?${queryParams.toString()}`;
      this.router.navigateByUrl(fullUrl);

    } else if (x['inactiveDoc']) {
      localStorage.setItem('O3RightMenuId', 'M-247');
      localStorage.setItem('current_menu_id', 'M-247');
      this.router.navigate(['/DM/Documents'], {
        queryParams: { inactiveDoc: true, ptId: x['ptId'], ref: x['ref'] },
      });
    } else {
      this.router.navigate(['/landingPage']);
    }
  }

  private preventBackButton() {
    setTimeout(() => {
      history.pushState(null, null, location.href);
      this.locationStrategy.onPopState(() => {
        history.pushState(null, null, location.href);
      });
    }, 1000);
  }

  isLoggedIn() {
    const isValidToken: JWTTokenValidation =
      this.jwtDecoderService.isThereValidToken();
    if (
      isValidToken === JWTTokenValidation.Expired ||
      isValidToken === JWTTokenValidation.NotFound
    ) {
      return false;
    }
    return true;
  }

  setUserConfigurations() {
    const user = this.baseApi.getUserSession();
    const userCode = user.userCode;
    const plantCode = this.baseApi.getPlantCodeFromStorage();
    const groupCode = this.baseApi.getGroupCodeFromStorage();
    this.getUserConfiguration(userCode, plantCode);
    this.getChatBotConfigurations(userCode, plantCode, groupCode);
  }

  setUserChatBotConfigurations(userChatbotConfiguration: any) {
    this.baseApi.setUserChatBotConfigurations(userChatbotConfiguration);
  }

  getUserChatbotConfiguration(key: any): Observable<any> {
    return this.http.post<any>(this.baseApi.encodeURL(Constants.API_CHATBOT_CONFIGURATIONS_GET), key);
  }

  addNewMenus(userMenus: any[]): void {
    const newMenus = [
      {
        menuId: null,
        parentId: 'null',
        type: 'link',
        label: 'User Plants',
        icon: 'analytics.svg',
        route: 'user-access/user-plants',
        applicationVariant: 'Core',
        children: [],
      },
      {
        menuId: null,
        parentId: 'null',
        type: 'link',
        label: 'User Profile',
        icon: 'analytics.svg',
        route: 'user-access/user-profile',
        applicationVariant: 'Core',
        children: [],
      },
      {
        menuId: null,
        parentId: 'null',
        type: 'link',
        label: 'Page not Found',
        icon: 'analytics.svg',
        route: '/not-found',
        applicationVariant: 'Core',
        children: [],
      },
    ];

    // Create a set of existing normalized routes
    const existingRoutes = new Set<string>(
      userMenus.map((menu) => {
        const menuRoute =
          menu.type === 'subheading' ? menu.route : `/${menu.route}`;
        return menuRoute.replace(/^\/+|\/+$/g, '');
      })
    );

    // Add new menus if they don't exist
    newMenus.forEach((newMenu) => {
      const normalizedNewMenuRoute = newMenu.route.replace(/^\/+|\/+$/g, '');
      if (!existingRoutes.has(normalizedNewMenuRoute)) {
        userMenus.push(newMenu);
      }
    });
  }

  checkMenuAccess(url: string): boolean {
    const userMenus = this.baseApi.getUserMenusSession();

    // Add new menus to userMenus
    this.addNewMenus(userMenus);

    // Normalize the input URL
    //const normalizedUrl = url.replace(/^\/+|\/+$/g, '');
    const normalizedUrl = url.split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '');
    // Function to check if the URL is accessible
    const checkUrlInMenus = (menus: any[]): boolean => {
      for (const menu of menus) {
        const menuRoute =
          menu.type === 'subheading' ? menu.route : `/${menu.route}`;
        // const normalizedMenuRoute = menuRoute.replace(/^\/+|\/+$/g, '');
        const normalizedMenuRoute = menuRoute.split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '');
        if (normalizedMenuRoute === normalizedUrl) {
          return true;
        }
        if (menu.children && menu.children.length > 0) {
          if (checkUrlInMenus(menu.children)) {
            return true;
          }
        }
      }
      return false;
    };

    // Check if the URL is accessible in the updated menus
    return checkUrlInMenus(userMenus);
  }

  getProfileInformation() {
    this.userService.getUserProfile().subscribe({
      next: (response) => {
        const userProfile = response;
        this.defaultPlant = userProfile.userPlants.find(
          (plant: any) => plant.isDefault === true
        );
      },
      error: ({ error }) => {
        console.log(error);
      },
    });
  }

  getUserConfiguration(userCode: string, plantCode: string) {
    const companyData = {
      PlantCode: plantCode,
      UserCode: userCode,
    };
    this.spinner.show();
    this.userService.getUserConfiguartion(companyData).subscribe(
      (respose) => {
        this.userconfiguration = respose[0];
        console.log(this.userconfiguration);
        if (this.userconfiguration) {
          const data: any = this.userconfiguration;
          const configData = {
            PlantCode: data.plantCode,
            UserCode: data.userCode,
            DateFormat: data.dateFormat,
            TimeFormat: data.timeFormat,
            CurrencyId: data.currencyCode,
            reportNumberFormat: data.reportNumberFormat,
            currencySymbol: data.currencySymbol,
          };
          this.baseApi.setUserConfigurations(configData);
        }
        this.spinner.hide();
      },
      ({ error }) => {
        this.spinner.hide();
        this.toaster.error('Error', error.message);
      }
    );
  }

  getChatBotConfigurations(userCode: string, plantCode: string, groupCode: string): void {
    const key = {
      userCode: userCode,
      plantCode: plantCode,
      groupCode: groupCode,
      createdBy: userCode
    }
    this.getUserChatbotConfiguration(key).subscribe(
      (response: any) => {
        this.spinner.hide();
        this.baseApi.removeUserChatBotConfigurations();
        this.setUserChatBotConfigurations(response);
      },
      (error: any) => {
        this.spinner.hide();
        this.toaster.error(error.message);
      }
    );
  }
}

